import { useState, useEffect } from 'react'
import { DateInput, SelectYesNo } from '../../components'
import { Spinner } from '@fluentui/react'
import TrusteeForm from './trustee-form'
import {
  IStreetAddress,
  propertyOwnershipStatus,
  StreetAddress,
  Validate,
} from '@oneethos/shared'
import { ConsumerAppFormData, SubformProps, Validation } from '.'
import HomeownersInsuranceForm, { homeownersInsuranceFieldErrors } from './homeowners-insurance-form'

const hasSavedCoveredAddress = (address?: IStreetAddress): boolean => {
  if (!address) {
    return false
  }

  return Boolean(
    address.streetAddress?.trim()
    || address.streetAddressLine2?.trim()
    || address.city?.trim()
    || address.state?.trim()
    || address.zip?.trim()
    || address.country?.trim()
  )
}

const ownershipDateError = (value: string, asOf: Date): string => {
  if (!value.trim()) {
    return 'Please enter the date you became the owner of the property.'
  }

  if (Validate.isValidDate(value)) {
    return 'Ownership date must be a valid mm/dd/yyyy date.'
  }

  if (propertyOwnershipStatus(value, asOf).isFuture) {
    return 'Ownership date cannot be in the future.'
  }

  return ''
}

/**
 * A date still being typed is not a finished answer. Keep insurance the
 * borrower already entered until the date is empty or a complete date
 * outside the 90-day window.
 */
const insuranceClearPatch = (
  ownershipDate: string,
  asOf: Date,
): { homeownersInsurance: null } | undefined => {
  const trimmedDate = ownershipDate.trim()
  if (trimmedDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(trimmedDate)) {
    return undefined
  }

  if (propertyOwnershipStatus(trimmedDate, asOf).insuranceRequired) {
    return undefined
  }

  return { homeownersInsurance: null }
}

const validatePropertyOwnership = (
  formData: ConsumerAppFormData,
  asOf: Date,
): Record<string, string> => {
  const results: Record<string, string> = {}
  const ownershipDate = formData.propertyOwnershipDate || ''
  const dateError = ownershipDateError(ownershipDate, asOf)

  if (dateError) {
    results.propertyOwnershipDate = dateError
  } else if (propertyOwnershipStatus(ownershipDate, asOf).insuranceRequired) {
    Object.assign(results, homeownersInsuranceFieldErrors(formData.homeownersInsurance, asOf))
  }

  if (!['yes', 'no'].includes(formData.propertyHeldInTrust)) {
    results.propertyHeldInTrust = 'Please indicate whether or not the property is held in a trust.'
  }

  return results
}

const PropertyOwnership = ({
  formData,
  update,
  saving,
  save
}: SubformProps) => {
  const [showTrusteeForm, setShowTrusteeForm] = useState(false)
  const [validation, setValidation] = useState<Record<string, string>>({})
  const asOf = new Date()
  const ownershipDate = formData.propertyOwnershipDate || ''
  const insuranceRequired = propertyOwnershipStatus(ownershipDate, asOf).insuranceRequired

  useEffect(() => {
    if (formData.propertyHeldInTrust === 'no' && formData.trustees.length > 0) {
      update('trustees', [])
    }
  }, [formData.propertyHeldInTrust, update])

  useEffect(() => {
    if (!insuranceRequired) {
      return
    }

    // Keep an address the applicant already entered. Prefill only while it is still empty.
    if (hasSavedCoveredAddress(formData.homeownersInsurance?.coveredAddress)) {
      return
    }

    if (!hasSavedCoveredAddress(formData.installationAddress)) {
      return
    }

    update('homeownersInsurance', {
      ...(formData.homeownersInsurance || {}),
      coveredAddress: { ...formData.installationAddress },
    })
  }, [
    insuranceRequired,
    formData.homeownersInsurance,
    formData.installationAddress,
    update,
  ])

  const address = StreetAddress.toString(formData.installationAddress)

  const isPropertyHeldInTrust = formData.propertyHeldInTrust === 'yes' ? true : false
  const hasCoborrower = formData.isJointApp
  const borrower = `${formData.firstName} ${formData.lastName}`
  const coBorrower = `${formData.coFirstName} ${formData.coLastName}`
  const hasFourTrustees = formData.trustees.length >= 4

  const additionalTrustees = formData.trustees?.filter(t =>
    t.email !== formData.email && t.email !== formData.coEmail)

  const handleTrusteeOnChange = (
    isChecked: boolean,
    name: string,
    email: string,
    address: IStreetAddress,
    phone: string
  ) => {
    if (isChecked) {
      if (!formData.trustees.some(t => t.name === name)) {
        update('trustees', [...formData.trustees, {
          name,
          email,
          phone,
          address,
        }])
      }
    } else {
      update('trustees', formData.trustees.filter(t => t.name !== name))
    }
  }

  const handleShowTrusteeForm = () => {
    setShowTrusteeForm(true)
  }

  const handleTrusteeFormClose = () => {
    setShowTrusteeForm(false)
  }

  const handleOwnershipDateChange = (value: string): void => {
    update({
      propertyOwnershipDate: value,
      ...insuranceClearPatch(value, new Date()),
    })
    setValidation({})
  }

  const onValidation = () => {
    const v = validatePropertyOwnership(formData, new Date())
    setValidation(v)
    if (Object.keys(v).length === 0) {
      save()
    }
  }

  return (
    <div>
      <h2>Property Ownership</h2>
      <div className="form-group">
        <label className="form-label">When did you become the owner of the property at <b>{address}</b>?</label>
        <DateInput
          value={ownershipDate}
          onChange={handleOwnershipDateChange}
          validator={value => ownershipDateError(value, new Date())}
        />
      </div>
      {insuranceRequired ? (
        <HomeownersInsuranceForm
          insurance={formData.homeownersInsurance || undefined}
          installationAddress={address}
          onChange={insurance => {
            update('homeownersInsurance', insurance)
            setValidation({})
          }}
        />
      ) : null}
      <div className="form-group">
        <label className="form-label">Is the property where the equipment will be located held in a trust?</label>
        <SelectYesNo
          value={formData.propertyHeldInTrust || ''}
          onChange={(val) => {
            update('propertyHeldInTrust', val)
            setValidation({})
          }}
        />
      </div>
      {isPropertyHeldInTrust ?
        <>
          <label>Select any borrowers who are trustees and add any additional trustees not in the loan.</label>
          <div className="input-group mb-3">
            <div className="input-group-text">
              <input
                className="form-check-input mt-0"
                type="checkbox"
                checked={formData.trustees.some(t => t.name === borrower)}
                onChange={(e) => {
                  handleTrusteeOnChange(
                    e.target.checked,
                    borrower,
                    formData.email,
                    formData.userAddress,
                    formData.phone
                  )
                }}
              />
            </div>
            <input type="text" className="form-control" value={borrower} readOnly />
          </div>
          {hasCoborrower &&
            <div className="input-group mb-3">
              <div className="input-group-text">
                <input
                  className="form-check-input mt-0"
                  type="checkbox"
                  checked={formData.trustees.some(t => t.name === coBorrower)}
                  onChange={(e) => {
                    handleTrusteeOnChange(
                      e.target.checked,
                      coBorrower,
                      formData.coEmail,
                      formData.coBorrowerAddress,
                      formData.coPhone
                    )
                  }}
                />
              </div>
              <input type="text" className="form-control" value={coBorrower} readOnly />
            </div>
          }
          {formData.trustees.length > 0 && (
            <div className="form-group">
              {additionalTrustees.map((trustee, index) => (
                <TrusteeForm
                  key={index}
                  update={update}
                  formData={formData}
                  onClose={() => {
                    update('trustees', formData.trustees.filter((_, i) => i !== index))
                  }}
                  initialData={trustee}
                  isEditing={true}
                />
              ))}
            </div>
          )}
          {showTrusteeForm && (
            <TrusteeForm
              update={update}
              formData={formData}
              onClose={handleTrusteeFormClose}
            />
          )}
        </>
        : null}
      {!hasFourTrustees && !showTrusteeForm && isPropertyHeldInTrust && (
        <div className="form-group">
          <button
            className='btn btn-secondary'
            type='button'
            onClick={handleShowTrusteeForm}
          >
            Add a new trustee
          </button>
        </div>
      )}
      {!showTrusteeForm &&
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving || (isPropertyHeldInTrust && formData.trustees.length === 0)}
          onClick={onValidation}
        >
          {saving ? <Spinner /> : 'Continue'}
        </button>}
      {validation && Object.keys(validation).length > 0 ? (
        <Validation validation={validation} />
      ) : null}
    </div>
  )
}

export default PropertyOwnership
